/**
 * ChartFilterDefs — global SVG <defs> for shared chart filter effects.
 *
 * Mount once near the top of ChartShell so every chart rendered inside
 * (regardless of renderer) can reference these filters by ID:
 *
 *   <circle filter="url(#dv-hover-halo)" ... />
 *   <ReferenceLine style={{ filter: 'url(#dv-mcl-glow)' }} ... />
 *
 * Filters defined here:
 *   dv-hover-halo    — soft alpha+blur halo for hovered data points
 *   dv-mcl-glow      — subtle outer glow for regulatory limit lines
 *   dv-mcl-glow-hot  — stronger glow used for the exceedance signature moment (H9)
 *   dv-point-glow    — bright glow used for points that crossed an MCL
 *
 * Implementation note: a single hidden 0x0 SVG holds the <defs>. SVG IDs
 * are global — duplicate defs (multiple ChartShells on one page) are
 * harmless as long as each definition is identical. Filters are still
 * usable across separate SVG roots (referenced by `url(#id)`).
 */

export function ChartFilterDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: 'absolute', pointerEvents: 'none' }}
      aria-hidden="true"
      focusable="false"
    >
      {/* H9 — global keyframes for the regulatory exceedance signature
          moment: a brief shudder runs through the chart and limit-line
          pulse runs to draw the eye. Animation names are namespaced so
          they can't collide with app keyframes. */}
      <style>
        {`
          @keyframes dv-chart-shudder {
            0%   { transform: translate(0, 0); }
            18%  { transform: translate(-1.2px, -0.4px); }
            36%  { transform: translate(1.6px, 0.6px); }
            54%  { transform: translate(-1px, 0.5px); }
            72%  { transform: translate(0.6px, -0.4px); }
            100% { transform: translate(0, 0); }
          }
          @keyframes dv-mcl-pulse {
            0%   { opacity: 0.55; stroke-width: 1; }
            30%  { opacity: 1;    stroke-width: 2; }
            100% { opacity: 1;    stroke-width: 1; }
          }
        `}
      </style>
      <defs>
        {/* Hover halo: soft 3px blur + merge with source so the original
            point stays crisp on top of the glow. */}
        <filter id="dv-hover-halo" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="0 0 0 0 0.063
                    0 0 0 0 0.725
                    0 0 0 0 0.506
                    0 0 0 0.6 0"
            result="emeraldBlur"
          />
          <feMerge>
            <feMergeNode in="emeraldBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Bright point glow for exceedance moments — same shape as
            hover-halo but stronger blur + higher alpha. Used by H9. */}
        <filter id="dv-point-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="0 0 0 0 0.937
                    0 0 0 0 0.267
                    0 0 0 0 0.267
                    0 0 0 0.85 0"
            result="redBlur"
          />
          <feMerge>
            <feMergeNode in="redBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* MCL line glow — subtle, doubles the line's visual weight without
            making it visually loud. Default state for limit lines. */}
        <filter id="dv-mcl-glow" x="-20%" y="-50%" width="140%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Hot MCL glow — used during exceedance signature moment (H9)
            when a value just crossed the limit. Pulses via CSS animation
            applied to the line element. */}
        <filter id="dv-mcl-glow-hot" x="-50%" y="-100%" width="200%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="0 0 0 0 1
                    0 0 0 0 0.4
                    0 0 0 0 0.4
                    0 0 0 0.9 0"
            result="hotBlur"
          />
          <feMerge>
            <feMergeNode in="hotBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
}
